'use client';

import { useRef, type ReactNode } from 'react';
import { useMaskedReveal } from '@/components/sections/kit/motion';
import { H2, SectionTag } from './bits';

/**
 * Section head for the dossier — mono two-tone number tag, onest-800 H2,
 * optional lead. Self-choreographs: masked SplitText line reveal on the H2
 * (fonts-ready, descender-safe, reverts on complete) while tag + lead settle
 * in behind. Static under prefers-reduced-motion.
 */
export default function DossierHead({
  num,
  label,
  title,
  lead,
  tone = 'ink',
  leadClassName = 'max-w-[560px]',
  titleClassName = '',
  children,
}: {
  num: string;
  label: string;
  title: ReactNode;
  lead?: ReactNode;
  tone?: 'ink' | 'light';
  /** Width clamp for the lead paragraph (per-section in the handoff). */
  leadClassName?: string;
  titleClassName?: string;
  /** Extra content inside the reveal root (rendered after the lead). */
  children?: ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  useMaskedReveal(rootRef, { heading: 'h2' });

  return (
    <div ref={rootRef}>
      <span data-head-sub className="block">
        <SectionTag num={num} label={label} tone={tone} />
      </span>
      <h2 className={`${H2} ${tone === 'light' ? 'text-white' : 'text-ink'} ${titleClassName}`}>
        {title}
      </h2>
      {lead && (
        <p
          data-head-sub
          className={`mt-4 text-[15.5px] leading-[1.65] text-pretty ${
            tone === 'light' ? 'text-white/72' : 'text-muted'
          } ${leadClassName}`}
        >
          {lead}
        </p>
      )}
      {children}
    </div>
  );
}
